import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import API from "../services/api";
import { LimitExclusion, Product, State } from "../types/brochure";
import { useAppStore } from "../session/store";
import { BatchRequest, DuplicateRequest, ReorderRequest } from "../types/api";

const QUERY_KEY_LIST_LIMIT_EXCLUSIONS = "listLimitExclusions";

const getListLimitExclusions = async (product?: Product, state?: State) => {
  if (!product?.id || !state?.id) {
    return Promise.resolve([]);
  }
  return API.services.getListLimitExclusions(state, product);
};

export const useListLimitExclusions = () => {
  const appStore = useAppStore();
  const { product, state } = appStore;
  return useQuery<LimitExclusion[], Error>(
    [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id, state?.id],
    () => getListLimitExclusions(product, state),
    {
      enabled: !!product?.id && !!state?.id,
    },
  );
};

export const useAddLimitExclusion = () => {
  const appStore = useAppStore();
  const { product, state } = appStore;
  const queryClient = useQueryClient();
  return useMutation<LimitExclusion, Error, LimitExclusion>(
    (body: LimitExclusion) =>
      API.services.postLimitExclusion({
        ...body,
        product_id: product?.id,
        state_id: state?.id,
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id, state?.id],
        });
      },
    },
  );
};

export const useUpdateLimitExclusion = () => {
  const appStore = useAppStore();
  const { product, state } = appStore;
  const queryClient = useQueryClient();
  return useMutation<LimitExclusion, Error, LimitExclusion>(
    (body: LimitExclusion) =>
      API.services.putLimitExclusion({ ...body, product_id: product?.id, state_id: state?.id }),
    {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id, state?.id],
        });
      },
    },
  );
};

export const useUpdateLimitExclusionBatch = () => {
  const appStore = useAppStore();
  const { product, state } = appStore;
  const queryClient = useQueryClient();
  return useMutation<LimitExclusion, Error, BatchRequest<LimitExclusion>>(
    ({ body, states }) =>
      API.services.postLimitExclusionBatch(
        { ...body, product_id: product?.id, state_id: state?.id },
        states,
      ),
    {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id],
        });
      },
    },
  );
};

export const useDeleteLimitExclusion = () => {
  const appStore = useAppStore();
  const { product } = appStore;
  const queryClient = useQueryClient();
  return useMutation<LimitExclusion, Error, LimitExclusion>(
    (body: LimitExclusion) => API.services.deleteLimitExclusion(Number(body.id)),
    {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id],
        });
      },
    },
  );
};

export const useReorderLimitExclusion = () => {
  const appStore = useAppStore();
  const { product } = appStore;
  const queryClient = useQueryClient();
  return useMutation<LimitExclusion, Error, ReorderRequest<LimitExclusion>>(
    ({ body, oldIndex, targetIndex }) =>
      API.services.reorderLimitExclusion(body, oldIndex, targetIndex),
    {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id],
        });
      },
    },
  );
};

const duplicateLimitExclusions = async ({ product, fromState, toStates }: DuplicateRequest) => {
  if (!product?.id || !fromState?.id) {
    return Promise.resolve(undefined);
  }
  return API.services.duplicateLimitExclusions(fromState, toStates, product);
};

export const useDuplicateLimitExclusions = () => {
  const { product } = useAppStore();
  const queryClient = useQueryClient();
  return useMutation<LimitExclusion | undefined, Error, DuplicateRequest>(
    req => duplicateLimitExclusions(req),
    {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_LIST_LIMIT_EXCLUSIONS, product?.id],
        });
      },
    },
  );
};
